import styled from "@emotion/styled";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";

import {
  Button,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_NORMAL,
  FONT_WEIGHT_SEMI_BOLD,
  Icon,
  IconButton,
  MobileStepper,
} from "@skilitics/designer-ui-components";
import { useDynamicFormatString, useFormatMessage } from "@skilitics/localization";

import { EmphasizedMessage } from "../composable/message/EmphasizedMessage";
import type { TutorialContent } from "./useTutorialData";

type Props = {
  tutorialContents: TutorialContent[];
  onClose: (neverShowTutorials?: boolean) => void;
};

export const TutorialContents = ({ tutorialContents, onClose }: Props) => {
  const formatDynamicMessage = useDynamicFormatString();
  const formatMessage = useFormatMessage(formatStrings);

  const [activeStep, setActiveStep] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = back

  const handleNext = () => {
    setDirection(1);
    setActiveStep((prevStep) => Math.min(prevStep + 1, tutorialContents.length - 1));
  };

  const handleBack = () => {
    setDirection(-1);
    setActiveStep((prevStep) => Math.max(prevStep - 1, 0));
  };

  if (!tutorialContents.length) return null;

  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 800 : -800,
      opacity: 0.5,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir: number) => ({
      x: dir < 0 ? 800 : -800,
      opacity: 0.5,
    }),
  };

  const { title, listItems, actionLabel, videoUrl, imageUrl } = tutorialContents[activeStep] || {};
  const videoType = videoUrl?.endsWith(".mp4") ? "video/mp4" : "video/webm"; // Glenn will add mp4 or webm files.

  return (
    <Layout>
      <OverflowContainer>
        <AnimatePresence mode="popLayout" custom={direction} initial={false}>
          <ContentContainer
            key={activeStep}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25, ease: "easeInOut" }}
          >
            <Heading>
              <EmphasizedMessage content={formatDynamicMessage(title)} components={{ emphasized: "strong" }} />
            </Heading>
            <SubHeadingContainer>
              {activeStep === 0 ? (
                <SubHeading>{formatMessage("subHeading")}</SubHeading>
              ) : (
                <Stepper
                  activeStep={activeStep}
                  steps={tutorialContents.length}
                  position="static"
                  variant="dots"
                  nextButton={null}
                  backButton={
                    <BackButton onClick={handleBack} color="primary" data-test="tutorial-back-button">
                      <Icon faPrefix="fas" icon="caret-left" />
                    </BackButton>
                  }
                  slotProps={{ dots: { sx: { gap: "8px" } } }}
                />
              )}
            </SubHeadingContainer>
            <Content>
              <ul>
                {listItems.map((item, idx) => (
                  <li key={`${activeStep}-${idx}`}>
                    <EmphasizedMessage content={formatDynamicMessage(item)} components={{ emphasized: "strong" }} />
                  </li>
                ))}
              </ul>
            </Content>
            <Actions>
              <CloseButton variant="outlined" onClick={() => onClose()}>
                {formatMessage("close")}
              </CloseButton>

              {actionLabel && (
                <PrimaryButton
                  variant="contained"
                  disabled={activeStep === tutorialContents.length - 1}
                  onClick={handleNext}
                >
                  {formatDynamicMessage(actionLabel)} <Icon faPrefix="fas" icon="caret-right" />
                </PrimaryButton>
              )}
            </Actions>
          </ContentContainer>
        </AnimatePresence>
      </OverflowContainer>

      <AnimatePresence mode="wait">
        <VideoOrImageContainer
          key={videoUrl ?? imageUrl}
          initial={{ opacity: 0.5 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0.5 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          {!!videoUrl && (
            <Video width="100%" autoPlay muted loop controls>
              <source src={videoUrl} type={videoType} />
              {formatMessage("noVideoWarning")}
            </Video>
          )}
          {!!imageUrl && <Image width="100%" src={imageUrl} />}
        </VideoOrImageContainer>
      </AnimatePresence>
    </Layout>
  );
};

const OverflowContainer = styled.div`
  position: relative;
  overflow: hidden; // Hide content as it animates outside of bounds.
`;

const Layout = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 525px;
  background: ${({ theme }) => theme.surface};
  height: 100%;
  min-width: 0;
  min-height: 0;
`;

const ContentContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  min-height: 0;
`;

const Heading = styled.h2`
  font-size: 28px;
  font-weight: ${FONT_WEIGHT_NORMAL};
  margin: 0;
  padding: 30px 30px 20px 30px;
  color: ${({ theme }) => theme.generalTextIcon};
  line-height: 1.4;
  > strong {
    display: block;
    font-weight: ${FONT_WEIGHT_BOLD};
  }
`;

const SubHeadingContainer = styled.div`
  align-self: flex-start;
  height: 34px;
  flex: 0 0 34px;
  height: 34px;
`;

const SubHeading = styled.h3`
  width: auto;
  line-height: 32px;
  margin: 0;
  padding: 0 30px;
  border-radius: 0 12px 12px 0;
  color: ${({ theme }) => theme.onNeutralContainer};
  background: ${({ theme }) => theme.neutralContainer};
  font-size: ${({ theme }) => theme.fontSizeLarge};
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
`;

const Stepper = styled(MobileStepper)`
  padding: 0 0 0 30px;
  gap: 8px;
`;

const Content = styled.div`
  flex: 1 1 100%;
  font-size: ${({ theme }) => theme.fontSizeLarge};
  padding: 20px 30px;
  overflow-y: auto;
  strong {
    font-weight: ${FONT_WEIGHT_SEMI_BOLD};
  }

  ul {
    margin: 0;
    padding: 0;
    li {
      margin: 0;
      padding: 0;
      margin-bottom: 15px;
      line-height: 1.4;
    }
  }
`;

const BackButton = styled(IconButton)`
  color: ${({ theme }) => theme.primary};
  border: solid 1px ${({ theme }) => theme.primary};
  border-radius: 20px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 0 30px 30px;
`;

const CloseButton = styled(Button)`
  height: 36px;
  border-radius: 20px;
  margin-right: 20px;
`;

const PrimaryButton = styled(Button)`
  background: ${({ theme }) => `linear-gradient(135deg, ${theme.primarySelection} 0%, ${theme.primary} 66%)`};
  color: ${({ theme }) => theme.onPrimary};
  border-radius: 20px 0 0 20px;
  height: 36px;
  &:disabled {
    color: ${({ theme }) => theme.onPrimary};
    opacity: 0.5;
  }
  .fa-icon-wrapper {
    margin-left: 8px;
    margin-bottom: -1px;
  }
`;

const VideoOrImageContainer = styled(motion.div)`
  height: 100%;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0 12px 12px 0;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0 12px 12px 0;
`;

const formatStrings = {
  subHeading: {
    en: "Core Area Orientation",
  },
  noVideoWarning: {
    en: "Your browser does not support videos.",
  },
  close: {
    en: "Close",
  },
};
